import React, { useState, useRef, useEffect } from "react";
import { useForm, ValidationError } from "@formspree/react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import AnimusHeroChrome from "../components/AnimusHeroChrome";
import "../styles.css";

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

const STREAMER_SUBJECTS = ["Streaming Collaboration"];

const SOCIAL_LINKS = [
  {
    href: "https://www.twitch.tv/zemaxi",
    icon: "fa-brands fa-twitch",
    label: "Twitch",
    handle: "zemaxi",
    color: "#9147ff",
  },
  {
    href: "https://www.youtube.com/@ZeMaxi",
    icon: "fa-brands fa-youtube",
    label: "YouTube",
    handle: "@ZeMaxi",
    color: "#ff0000",
  },
  {
    href: "https://ko-fi.com/zemaxi",
    icon: "fa-brands fa-ko-fi",
    label: "Ko-fi",
    handle: "zemaxi",
    color: "#00b9fe",
  },
  {
    href: "https://x.com/zemaxiirl",
    icon: "fa-brands fa-x-twitter",
    label: "X (Twitter)",
    handle: "@zemaxiirl",
    color: "#ffffff",
  },

];

export default function Contact() {
  const captchaRef = useRef(null);
  const [formspreeState, formspreeSubmit] = useForm(FORMSPREE_ID || "xnjgjvlq");

  const [subject, setSubject] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);

  const isStreamerSubject = STREAMER_SUBJECTS.includes(subject);

  useEffect(() => {
    if (formspreeState.succeeded) {
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    }
  }, [formspreeState.succeeded]);

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaError(false);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    const formEl = e.target;
    const data = new FormData(formEl);
    const payload = Object.fromEntries(data.entries());
    payload["h-captcha-response"] = captchaToken;
    await formspreeSubmit(payload);
  };

  return (
    <>
      <SEO
        title="Contact ZeMaxi"
        description="Get in touch with ZeMaxi for business inquiries, collaborations, sponsorships, or streaming tool support."
        keywords="contact ZeMaxi, streamer contact, business inquiries, collaboration, streaming support, sponsorship"
        url="https://zemaxi.com/contact"
      />
      <Navbar />
      <main>
        <section className="contact-hero animus-hero animus-hero-bg">
          <AnimusHeroChrome />
          <div className="container">
            <p className="animus-hero-id font-mono">{'// SECTION_CONTACT'}</p>
            <h1>Get In <span className="highlight">Contact</span></h1>
            <p>Business inquiries, collaborations, or other questions? I'd love to hear from you.</p>
          </div>
        </section>

        <section className="contact-section page-body-section">
          <div className="container">
            <div className="contact-grid">

              {/* === FORM COLUMN === */}
              <div className="contact-form-wrap">
                <h2>Send a Message</h2>

                {formspreeState.succeeded ? (
                  <div className="contact-success">
                    <i className="fas fa-check-circle"></i>
                    <h3>Message Sent!</h3>
                    <p>Thanks for reaching out. I'll get back to you as soon as possible.</p>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleSubmit} noValidate>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Name <span className="required">*</span></label>
                        <input
                          id="name"
                          type="text"
                          name="name"
                          placeholder="Your name"
                          required
                          autoComplete="name"
                        />
                        <ValidationError prefix="Name" field="name" errors={formspreeState.errors} className="contact-error" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email <span className="required">*</span></label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          placeholder="your@email.com"
                          required
                          autoComplete="email"
                        />
                        <ValidationError prefix="Email" field="email" errors={formspreeState.errors} className="contact-error" />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">Subject <span className="required">*</span></label>
                      <select
                        id="subject"
                        name="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                      >
                        <option value="">Select a subject...</option>
                        <option value="General Enquiry">General Enquiry</option>
                        <option value="Business Enquiry">Business Enquiry</option>
                        <option value="Streaming Collaboration">Streaming Collaboration</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {isStreamerSubject && (
                      <div className="contact-streamer-fields">
                        <div className="streamer-fields-label">
                          <i className="fa-solid fa-broadcast-tower"></i> Streamer Details
                        </div>
                        <div className="form-group">
                          <label htmlFor="platform">Primary Platform</label>
                          <select id="platform" name="platform">
                            <option value="">Select platform...</option>
                            <option value="Twitch">Twitch</option>
                            <option value="YouTube">YouTube</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor="channelUrl">Channel / Social URL</label>
                          <input
                            id="channelUrl"
                            type="url"
                            name="channelUrl"
                            placeholder="https://twitch.tv/yourchannel"
                          />
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="message">Message <span className="required">*</span></label>
                      <textarea
                        id="message"
                        name="message"
                        placeholder="Tell me more about your enquiry..."
                        rows={6}
                        required
                      />
                      <ValidationError prefix="Message" field="message" errors={formspreeState.errors} className="contact-error" />
                    </div>

                    <div className="form-captcha">
                      <HCaptcha
                        sitekey={HCAPTCHA_SITE_KEY || "10000000-ffff-ffff-ffff-000000000001"}
                        onVerify={handleCaptchaVerify}
                        onExpire={handleCaptchaExpire}
                        ref={captchaRef}
                        theme="dark"
                      />
                    </div>

                    {captchaError && (
                      <div className="contact-error">
                        <i className="fas fa-exclamation-triangle"></i> Please complete the CAPTCHA verification.
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary contact-submit"
                      disabled={formspreeState.submitting}
                    >
                      {formspreeState.submitting ? (
                        <><i className="fas fa-spinner fa-spin"></i> Sending...</>
                      ) : (
                        <><i className="fas fa-paper-plane"></i> Send Message</>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* === SIDEBAR COLUMN === */}
              <div className="contact-sidebar">
                <div className="contact-info-card">
                  <h3>Connect With Me</h3>
                  <p>Follow along for streaming, gaming content, and tools built for streamers.</p>
                  <ul className="contact-socials">
                    {SOCIAL_LINKS.map((link) => (
                      <li key={link.label}>
                        <a href={link.href} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                          <span className="contact-social-icon" style={{ color: link.color }}>
                            <i className={link.icon}></i>
                          </span>
                          <span className="contact-social-text">
                            <span className="contact-social-platform">{link.label}</span>
                            <span className="contact-social-handle">{link.handle}</span>
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
